import React from 'react';

interface HomestreamLogoProps {
  size?: 'small' | 'medium' | 'large';
  variant?: 'light' | 'dark';
  onClick?: () => void;
  /** Hard white drop shadow under the capsule (offset 3px right / 4px down, no blur), in
   *  white so it shows on the dark navy header. */
  dropShadow?: boolean;
}

export default function HomestreamLogo({
  size = 'medium',
  variant = 'light',
  onClick,
  dropShadow = false,
}: HomestreamLogoProps) {
  // Wide capsule badge around the HOMESTREAM wordmark.
  const getLogoSize = () => {
    switch (size) {
      case 'small':
        return { width: 108, height: 26, borderWidth: 2  , imageSize: 98, glow: 7 };
      case 'large':
        return { width: 180, height: 60, borderWidth: 2.5, imageSize: 160, glow: 12 };
      default:
        return { width: 126, height: 42, borderWidth: 2.25, imageSize: 120, glow: 10 };
    }
  };

  const logoSize = getLogoSize();
  const isLight = variant === 'light';
  // Soft glow around the border and wordmark (white on dark, faint black on light).
  const glowRgb = isLight ? '255, 255, 255' : '0, 0, 0';

  const badgeStyle: React.CSSProperties = {
    width: logoSize.width,
    height: logoSize.height,
    borderRadius: '999px',
    border: `${logoSize.borderWidth}px solid ${isLight ? '#FFFFFF' : '#000000'}`,
    boxSizing: 'border-box',
    boxShadow: [
      `0 0 ${logoSize.glow}px rgba(${glowRgb}, ${isLight ? 0.55 : 0.2})`,
      `inset 0 0 ${logoSize.glow * 0.75}px rgba(${glowRgb}, ${isLight ? 0.3 : 0.1})`,
      // Hard white shadow offset right + down, no blur.
      ...(dropShadow ? ['3px 4px 0 rgba(255, 255, 255, 0.8)'] : []),
    ].join(', '),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'transform 0.2s, opacity 0.2s',
  };

  
  const imageStyle: React.CSSProperties = {
    width: logoSize.imageSize,
    height: logoSize.imageSize,
    objectFit: 'contain',
    filter: `${isLight ? 'brightness(0) invert(1) ' : ''}drop-shadow(0 0 ${logoSize.glow * 0.5}px rgba(${glowRgb}, ${isLight ? 0.7 : 0.25}))`,
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div style={containerStyle}>
      <div
        style={badgeStyle}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (onClick) {
            e.currentTarget.style.opacity = '0.8';
            e.currentTarget.style.transform = 'scale(1.05)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <img
          src="/HomeStream.png"
          alt="Homestream"
          style={imageStyle}
        />
      </div>
    </div>
  );
}
